import {
  DEFAULT_ACCENT_NAME,
  DEFAULT_COLOR_NAME,
  DEFAULT_TEXT_ON_ACCENT_NAME,
  Navbar2Element,
  NavbarButton,
  NavbarDropdown,
  NavbarDropdownMenuColumn,
  NavbarDropdownMenuItem,
  NavbarItem,
  NavbarLink,
} from '@typedream/core';
import { cloneDeep } from 'lodash';
import { NAVBAR_ITEM_TYPE } from '../../../../constants';
import { makeInitialMenuValue } from './dropdownInitialValues';

/**
 * Update an item in the array at a given position with a new value.
 *
 * @template T - The type of array elements.
 * @param {Array<T>} arr - The input array to be updated.
 * @param {number} index - The position of the item to be updated in the array.
 * @param {T} newValue - The new value to be assigned to the item at the specified index.
 * @returns {Array<T>} - The updated array with the new value at the specified index.
 */
export function updateArrayItemByPosition<T>(arr: Array<T>, index: number, newValue: T) {
  return arr.map((item, i) => {
    if (i !== index) return item;
    return newValue;
  });
}

export function updateMenuItemByType(currentMenuItem: NavbarItem, newMenuType: NavbarItem['type']): NavbarItem {
  switch (newMenuType) {
    case 'link': {
      return {
        ...currentMenuItem,
        color: DEFAULT_COLOR_NAME,
        type: NAVBAR_ITEM_TYPE.LINK,
      } as NavbarLink;
    }

    case 'button': {
      return {
        ...currentMenuItem,
        background: DEFAULT_ACCENT_NAME,
        color: DEFAULT_TEXT_ON_ACCENT_NAME,
        type: NAVBAR_ITEM_TYPE.BUTTON,
      } as NavbarButton;
    }

    case 'dropdown': {
      return {
        ...currentMenuItem,
        menu: makeInitialMenuValue(),
        type: 'dropdown',
        color: DEFAULT_COLOR_NAME,
      } as NavbarDropdown;
    }

    default:
      return currentMenuItem;
  }
}

// TODO: Move to @typedream/core (?) or to the FSMs types.
type MenuType = 'brand' | 'menu' | 'endMenu';
type MachineContextWithNavbar = {
  dataNavbar?: Navbar2Element,
  menuIndex?: number,
  menuType?: MenuType,
};
type MachineContextWithNavbarDropdown = MachineContextWithNavbar & {
  // Changed by column setting toolbar
  selectedDropdownItemType?: 'column' | 'column_item',
  selectedColumnItemId?: string,
  selectedColumnId?: string,
  selectedColumnIdx?: number,
  selectedColumnItemIdx?: number,
};

/**
 * Get current selected menu from current FSM's context.
 * @template T - The type of FSM's context which must have `dataNavbar`,
 *               `menuIndex` and `menuType` properties.
 * @param context
 * @param {Navbar2Element} context.menuType - The type of the menu to be selected.
 * @param {number} context.dataNavbar - The navbar data.
 * @param {MenuType} context.menuIndex - The index of the menu to be selected.
 * @returns {NavbarDropdown}
 *
 * @example
 * const navbarMenuItem = getSelectedNavbarItem(context);
 *
 * @example
 * // Narrowing the type as `NavbarDropdown`
 * const navbarMenuItem = getSelectedNavbarItem<NavbarDropdown>(context);
 */
export function getSelectedNavbarItem<
  U extends NavbarItem,
  T extends MachineContextWithNavbar = MachineContextWithNavbar,
>(
  context: T,
) {
  if (
    context.menuType !== undefined
    && context.menuType !== 'brand'
    && context.dataNavbar?.[context.menuType] !== undefined
    && context.dataNavbar?.[context.menuType].length > 0
    && context?.menuIndex !== undefined
    && context?.dataNavbar?.[context.menuType][context.menuIndex]
  ) {
    return context?.dataNavbar?.[context.menuType][context.menuIndex] as U;
  }

  return undefined;
}

/**
 * Get current selected column from current FSM's context.
 * Will return undefined if there's no selected column.
 * @param context
 * @returns
 */
export function getSelectedDropdownColumn<T extends MachineContextWithNavbarDropdown>(
  context: T,
) {
  const navbarMenuItem = getSelectedNavbarItem(context);
  if (
    context?.selectedColumnId
    && navbarMenuItem?.type === 'dropdown'
  ) {
    // eslint-disable-next-line max-len
    const selectedColumn = navbarMenuItem?.menu.children.find((c) => c.id === context.selectedColumnId);
    return selectedColumn as NavbarDropdownMenuColumn | undefined;
  }

  return undefined;
}

/**
 * Return `navbarItem`, `column`, and `columnItem` references from current FSM's context.
 * @param context
 * @returns
 */
export function getSelectedNavbarDropdownItems<T extends MachineContextWithNavbarDropdown>(
  context: T,
):
  ({
    navbarItem: NavbarDropdown,
    column?: NavbarDropdownMenuColumn,
    columnItem?: NavbarDropdownMenuItem,
  } | undefined) {
  // This action used to add a new column item to the selected column.
  // But, since the `Navbar` is a single node, we need to go through the
  // `Navbar`'s node to find the selected column item.
  // This is the path to the selected column item:
  //
  // 1. Navbar
  // ├─ 2. Menu || EndMenu
  // │  ├─ 3. MenuItem
  // │  │  ├─ 4. MenuItem.menu
  // │  │  │  ├─ 5. MenuItem.menu.children
  // │  │  │  │  ├─ 6. MenuItemColumn | NavbarMenuItem
  // │  │  │  │  │  ├─ 7. MenuItemColumn.children <-- the selected column
  //
  // After we find the selected column item array (MenuItemColumn.children),
  // we can add a new column item to it based on the `context.selectedColumnItemIndex`.

  if (
    context.dataNavbar
    && context.menuType !== 'brand'
    && context.menuType !== undefined
    && context.menuIndex !== undefined
  ) {
    // 1. Navbar
    // ├─ 2. Menu || EndMenu
    // │  ├─ 3. MenuItem <-- `currentMenuItem`
    // Find the selected menu from the navbar's node using the `menuIndex`.
    // Made a copy of the selected menu.
    // eslint-disable-next-line max-len
    const currentMenuItem = context.dataNavbar[context.menuType][context.menuIndex] as NavbarDropdown;

    // │  │  ├─ 4. MenuItem.menu
    // │  │  │  ├─ 5. MenuItem.menu.children <-- `currentMenuItemChildren`
    const currentMenuItemChildren: NavbarDropdown['menu']['children'] = currentMenuItem.menu.children;

    // We wanted to insert the new column item into the selected column
    // or into the last column if there's no selected column.
    let columnTargetIndex = currentMenuItemChildren.findIndex((c) => c.id === context?.selectedColumnId ?? '');
    if (columnTargetIndex === -1) {
      columnTargetIndex = currentMenuItemChildren.length > 0
        ? currentMenuItemChildren.length - 1
        : 0;
    }

    // │  │  │  │  ├─ 6. MenuItemColumn | NavbarMenuItem <-- `currentColumn`
    // Find the selected column from the selected menu.
    // eslint-disable-next-line max-len
    const currentColumn = currentMenuItemChildren[columnTargetIndex] as NavbarDropdownMenuColumn;

    if (context.selectedDropdownItemType === 'column' || context.selectedDropdownItemType === undefined) {
      return {
        navbarItem: currentMenuItem,
        column: currentColumn,
      };
    }

    if (context.selectedDropdownItemType === 'column_item') {
      // If there's no selected column, the menu item might be
      // a featured menu item.
      if (
        context?.selectedColumnId === undefined
        && currentMenuItem.menu.children[0].type === 'navbar_dropdown_menu_item'
      ) {
        return {
          navbarItem: currentMenuItem,
          columnItem: currentMenuItem.menu.children[0],
        };
      }

      if (
        currentColumn?.children.length === 0
        || (
          currentColumn?.children.length === 1
          && currentColumn?.children[0]
          && 'text' in currentColumn.children[0]
        )
      ) {
        return undefined;
      }
      // Find the dropdown menu item in the selected column.
      // │  │  │  │  │  ├─ 7. MenuItemColumn.children <-- `currentColumnChildren`
      // Find the selected column item from the selected column.
      const currentColumnChildren = currentColumn.children as NavbarDropdownMenuItem[];

      // eslint-disable-next-line max-len
      const currentColumnItem = currentColumnChildren.find(
        (c) => c.id === context.selectedColumnItemId,
      );

      return {
        navbarItem: currentMenuItem,
        column: currentColumn,
        columnItem: currentColumnItem,
      };
    }
  }

  return undefined;
}

export function updateDropdownMenuItem<
  T extends MachineContextWithNavbarDropdown,
>(params: {
  context: T,
  newDropdownMenuItem: NavbarDropdownMenuItem,
}) {
  // Check deps
  const { context, newDropdownMenuItem } = params;

  if (
    context.dataNavbar === undefined
    || context.menuType === undefined
    || context.menuType === 'brand'
    || context.menuIndex === undefined
    || context.dataNavbar?.[context.menuType] === undefined
  ) return undefined;
  const navbarItem = context.dataNavbar[context.menuType][context.menuIndex] as NavbarDropdown;

  // Handle featured items
  if (newDropdownMenuItem.itemType === 'featured'
    && context?.selectedColumnIdx === undefined
    && context?.selectedColumnItemIdx !== undefined
  ) {
    const newNavbarItemChildren = updateArrayItemByPosition(
      navbarItem.menu.children,
      context.selectedColumnItemIdx,
      newDropdownMenuItem,
    ) as [NavbarDropdownMenuItem, ...NavbarDropdownMenuColumn[]];

    const newNavbarItem: NavbarDropdown = {
      ...navbarItem,
      menu: {
        ...navbarItem.menu,
        children: newNavbarItemChildren,
      },
    };

    const newMenuArr = updateArrayItemByPosition(
      context.dataNavbar?.[context.menuType],
      context.menuIndex ?? -1,
      newNavbarItem,
    );

    return newMenuArr;
  }

  // The DropdownMenuItem is a column's item
  if (
    context?.selectedColumnIdx !== undefined
    && context?.selectedColumnItemIdx !== undefined
  ) {
    const column = (
      context.dataNavbar[context.menuType][context.menuIndex] as NavbarDropdown
    ).menu.children[context.selectedColumnIdx] as NavbarDropdownMenuColumn;

    const newColumn = cloneDeep(column);

    newColumn.children = updateArrayItemByPosition(
      column.children as NavbarDropdownMenuItem[],
      context.selectedColumnItemIdx,
      newDropdownMenuItem,
    );

    const newNavbarItemChildren = updateArrayItemByPosition(
      navbarItem.menu.children,
      context.selectedColumnIdx,
      newColumn,
    ) as NavbarDropdownMenuColumn[] | [NavbarDropdownMenuItem, ...NavbarDropdownMenuColumn[]];

    const newNavbarItem: NavbarDropdown = {
      ...navbarItem,
      menu: {
        ...navbarItem.menu,
        children: newNavbarItemChildren,
      },
    };

    const newMenuArr = updateArrayItemByPosition(
      context.dataNavbar?.[context.menuType],
      context.menuIndex ?? -1,
      newNavbarItem,
    );

    return newMenuArr;
  }

  return undefined;
}
export function getUpdatedMenuItem<T extends MachineContextWithNavbarDropdown>(params: {
  context: T,
  navbarItem: NavbarDropdown,
  column?: NavbarDropdownMenuColumn,
  newColumnItem: NavbarDropdownMenuItem,
}) {
  const {
    context,
    newColumnItem,
    column,
    navbarItem,
  } = params;

  if (
    context.dataNavbar === undefined
    || context.menuType === undefined
    || context.menuType === 'brand'
    || context.menuIndex === undefined
    || context.dataNavbar?.[context.menuType] === undefined) return undefined;

  // Check if it's a featured item by checking if the `column` is undefined.
  if (newColumnItem.itemType === 'featured') {
    const newMenuArr = updateArrayItemByPosition(
      context.dataNavbar?.[context.menuType],
      context.menuIndex ?? -1,
      navbarItem,
    );

    return newMenuArr;
  }

  if (column !== undefined) {
    const columnItemIndex = column.children.findIndex(
      (item) => item.id === context.selectedColumnItemId,
    );

    const newColumn = cloneDeep(column);

    newColumn.children = updateArrayItemByPosition(
      column.children as NavbarDropdownMenuItem[],
      columnItemIndex,
      newColumnItem,
    );

    const columnIdx = navbarItem.menu.children.findIndex(
      (col) => col.id === column.id,
    );

    const newNavbarItemChildren = updateArrayItemByPosition(
      navbarItem.menu.children,
      columnIdx,
      newColumn,
    ) as NavbarDropdownMenuColumn[] | [NavbarDropdownMenuItem, ...NavbarDropdownMenuColumn[]];

    const newNavbarItem: NavbarDropdown = {
      ...navbarItem,
      menu: {
        ...navbarItem.menu,
        children: newNavbarItemChildren,
      },
    };

    const newMenuArr = updateArrayItemByPosition(
      context.dataNavbar?.[context.menuType],
      context.menuIndex ?? -1,
      newNavbarItem,
    );

    return newMenuArr;
  }

  return undefined;
}

export function isColumnEmpty(column: NavbarDropdownMenuColumn) {
  return column.children.length === 0
    || (
      column.children.length === 1
      && column.children[0]
      && 'text' in column.children[0]
    );
}
